import React, { useEffect, useState } from 'react';
import { 
    Card, 
    List, 
    Avatar, 
    Typography, 
    Badge, 
    Button, 
    Empty,
    Space,
    Tag
} from 'antd';
import { 
    MessageOutlined, 
    UserOutlined, 
    CalendarOutlined,
    EnvironmentOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../../../services/api';
import ChatInterface from '../../Chat/ChatInterface';

const { Title, Text } = Typography;

function CustomerChat() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLead, setSelectedLead] = useState(null);
    const [chatVisible, setChatVisible] = useState(false);

    useEffect(() => {
        fetchLeads();
    }, []);

    useEffect(() => {
        // Check if there's a lead parameter in the URL
        const leadId = searchParams.get('lead');
        if (leadId && leads.length > 0) {
            const lead = leads.find(l => l.id === parseInt(leadId));
            if (lead) {
                setSelectedLead(lead);
            }
        }
    }, [searchParams, leads]);

    const fetchLeads = async () => {
        try {
            setLoading(true);
            // Get leads for the current user
            const response = await api.getMyLeads();
            if (response.success) {
                setLeads(response.data.data || response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch leads:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenChat = (lead) => {
        setSelectedLead(lead);
        setChatVisible(true);
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            quoted: 'blue',
            accepted: 'green',
            rejected: 'red',
            completed: 'purple'
        };
        return colors[status] || 'default';
    };

    const getUnreadCount = (lead) => {
        // This would be calculated based on unread messages
        // For now, return 0 or implement based on your needs
        return 0;
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <Title level={2}>My Conversations</Title>
                <Text type="secondary">Chat with vendors about your booking requests</Text>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Leads List */}
                <div className="lg:col-span-1">
                    <Card title="Booking Requests" className="h-full">
                        {leads.length === 0 ? (
                            <Empty 
                                description="No booking requests yet" 
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ) : (
                            <List
                                dataSource={leads}
                                loading={loading}
                                renderItem={(lead) => (
                                    <List.Item
                                        className={`cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors ${
                                            selectedLead?.id === lead.id ? 'bg-blue-50 border border-blue-200' : ''
                                        }`}
                                        onClick={() => handleOpenChat(lead)}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <Badge count={getUnreadCount(lead)} size="small">
                                                    <Avatar 
                                                        icon={<UserOutlined />} 
                                                        style={{ backgroundColor: '#1890ff' }}
                                                    />
                                                </Badge>
                                            }
                                            title={
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium">{lead.vendor?.name || 'Vendor'}</span>
                                                    <Tag color={getStatusColor(lead.status)} size="small">
                                                        {lead.status}
                                                    </Tag>
                                                </div>
                                            }
                                            description={
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        <CalendarOutlined />
                                                        {lead.event_date} at {lead.event_time}
                                                    </div>
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        <EnvironmentOutlined />
                                                        {lead.venue}
                                                    </div>
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        <TeamOutlined />
                                                        {lead.guest_count} guests
                                                    </div>
                                                    {lead.final_total && (
                                                        <div className="text-sm font-medium text-green-600">
                                                            ₹{lead.final_total.toLocaleString()}
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </div>

                {/* Chat Interface */}
                <div className="lg:col-span-2">
                    {selectedLead ? (
                        <ChatInterface 
                            leadId={selectedLead.id} 
                            isVendor={false}
                        />
                    ) : (
                        <Card className="h-96 flex items-center justify-center">
                            <Empty 
                                description="Select a booking request to start chatting" 
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        </Card>
                    )}
                </div>
            </div>

            {/* Mobile Chat Modal */}
            {chatVisible && (
                <div className="lg:hidden fixed inset-0 z-50 bg-white">
                    <div className="flex items-center justify-between p-4 border-b">
                        <Title level={4} className="mb-0">
                            Chat with {selectedLead?.vendor?.name}
                        </Title>
                        <Button onClick={() => setChatVisible(false)}>
                            Close
                        </Button>
                    </div>
                    <div className="h-full">
                        {selectedLead && (
                            <ChatInterface 
                                leadId={selectedLead.id} 
                                isVendor={false}
                                onClose={() => setChatVisible(false)}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default CustomerChat;
